// This file is adapted from Kepler's implementation of color legends in
// https://github.com/CartoDB/carto-kepler/blob/main/src/components/common/color-legend.js
import { format } from 'd3-format'
import { SCALE_TYPES, ALL_FIELD_TYPES, UNKNOWN_COLOR } from '@carto/kepler.gl/constants'
import { CustomMarkersRange, KeplerScaleType } from '../types'
import { scaleQuantize, scaleQuantile, scaleOrdinal, scaleThreshold, scaleIdentity } from 'd3-scale'

interface ScaleFunction<T = number[]> {
  (value: number): T
  domain(domain: Array<number | null>): ScaleFunction<T>
  range(range: Array<T>): ScaleFunction<T>
  unknown(value: T): ScaleFunction<T>
}
type ScaleFactory = () => ScaleFunction

const scaleFunctionFactories: Partial<Record<KeplerScaleType, ScaleFactory>> = {
  [KeplerScaleType.Quantize]: scaleQuantize as ScaleFactory,
  [KeplerScaleType.Quantile]: scaleQuantile,
  [KeplerScaleType.Ordinal]: scaleOrdinal,
  [KeplerScaleType.Custom]: scaleThreshold,
  [KeplerScaleType.Identity]: scaleIdentity as ScaleFactory
}

const defaultFormat = (d) => d

const getNumericLabelFormat = (domain) => {
  const diff = domain[1] - domain[0]

  if (diff < 10) {
    return format('.2f')
  }

  return format('.1f')
}

const getQuantLabelFormat = (domain, fieldType) => {
  // quant scale can only be assigned to linear Fields: real, timestamp, integer
  return fieldType === ALL_FIELD_TYPES.timestamp
    ? defaultFormat
    : !fieldType
    ? defaultFormat
    : getNumericLabelFormat(domain)
}

const getOrdinalLegends = (scale) => {
  const domain = scale.domain()
  return {
    data: domain.map(scale),
    labels: domain.map((label) => {
      // In some situations, the domain is not a number, so we need to prevent NaN values
      const parsedNum = Number(label)
      return isNaN(parsedNum) ? label : parsedNum.toFixed(2)
    }),
    customMarkers: undefined
  }
}

const getQuantLegends = (scale, labelFormat, domain) => {
  if (typeof scale.invertExtent !== 'function') {
    // only quantile, quantize, threshold scale has invertExtent method
    return {
      data: [],
      labels: []
    }
  }
  const range = scale.range()
  const labels = range.map((d, index) => {
    const invert = scale.invertExtent(d)
    // For the threshold scale invertExtent function return null and undefined for the first and last value
    invert[0] = index === 0 ? domain[0] : invert[0]
    invert[1] = index === range.length - 1 ? domain[domain.length - 1] : invert[1]
    // Display only ">A" for cases of inconsistent last bucket "A - B" where B < A
    // (can happen with logarithmic scales, as their breaks don't depend on the domain)
    if (index === range.length - 1 && invert[1] < invert[0]) {
      return `>${labelFormat(invert[0])}`
    }
    return `${labelFormat(invert[0])} – ${labelFormat(invert[1])}`
  })

  return {
    data: scale.range(),
    labels
  }
}

export const colorLabeler = (
  domain,
  range,
  scaleType,
  fieldType,
  applyCustomMarkers,
  layerConfig
): {
  data: string[]
  labels: string[]
  customMarkers?: string[]
} => {
  const empty = {
    data: [],
    labels: []
  }
  if (!range) {
    return empty
  }
  if (Array.isArray(range.colors)) {
    if (!domain || !scaleType) {
      return empty
    }

    const scaleFunction = scaleFunctionFactories[scaleType]

    // We store custom breaks in range.colorMap

    let actualDomain: unknown[]
    let actualRange: unknown[]
    if (Array.isArray(range.colorMap)) {
      const cMap = new Map()
      range.colorMap.forEach(([k, v]) => cMap.set(k, v))
      actualDomain = Array.from(cMap.keys())
      actualRange = Array.from(cMap.values())
    } else {
      actualDomain = scaleType === SCALE_TYPES.ordinal ? domain.slice(0, range.colors.length) : domain
      actualRange = range.colors
    }

    const scale = scaleFunction().domain(actualDomain).range(actualRange).unknown(UNKNOWN_COLOR)

    if (scaleType === SCALE_TYPES.ordinal) {
      const legends = getOrdinalLegends(scale)
      if (!applyCustomMarkers) {
        return legends
      }
      return applyCustomMarkersToLegend(legends, actualDomain, layerConfig)
    }

    const formatLabel = getQuantLabelFormat(scale.domain(), fieldType)

    return getQuantLegends(scale, formatLabel, domain)
  }
  return empty
}

function applyCustomMarkersToLegend(
  legends: {
    data: []
    labels: []
  },
  domain: unknown[],
  layerConfig
) {
  if (!layerConfig.visConfig.customMarkers) {
    return legends
  }

  if ((layerConfig.colorField?.id, layerConfig.customMarkersField?.id && layerConfig.visConfig.customMarkersRange)) {
    const customMarkersRange: CustomMarkersRange = layerConfig.visConfig.customMarkersRange

    const fallbackIcon = customMarkersRange.othersMarker || layerConfig.visConfig.customMarkersUrl
    const customMarkersMapping = customMarkersRange.markerMap.reduce((r, e) => {
      r.set(e.value, e.markerUrl)
      return r
    }, new Map())

    return {
      ...legends,
      customMarkers: [...domain.map((key) => customMarkersMapping.get(key) || fallbackIcon), fallbackIcon]
    }
  }

  if (layerConfig.visConfig.customMarkersUrl) {
    return {
      ...legends,
      customMarkers: layerConfig.visConfig.customMarkersUrl
    }
  }
  return legends
}
